<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import LabelActions from 'dashboard/components/widgets/conversation/conversationBulkActions/LabelActions.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  visibleContactIds: {
    type: Array,
    default: () => [],
  },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'clearSelection',
  'assignLabels',
  'toggleAll',
  'deleteSelected',
]);

const { t } = useI18n();

const selectedCount = computed(() => props.selectedContactIds.length);
const totalVisibleContacts = computed(() => props.visibleContactIds.length);
const showLabelSelector = ref(false);

const selectAllLabel = computed(() => {
  if (!totalVisibleContacts.value) {
    return '';
  }

  return t('CONTACTS_BULK_ACTIONS.SELECT_ALL', {
    count: totalVisibleContacts.value,
  });
});

const selectedCountLabel = computed(() =>
  t('CONTACTS_BULK_ACTIONS.SELECTED_COUNT', {
    count: selectedCount.value,
  })
);

const allSelected = computed(() => {
  return (
    props.visibleContactIds.length > 0 &&
    props.selectedContactIds.length === props.visibleContactIds.length
  );
});

const toggleSelectAll = () => {
  emit('toggleAll', !allSelected.value);
};

const emitClearSelection = () => {
  showLabelSelector.value = false;
  emit('clearSelection');
};

const toggleLabelSelector = () => {
  if (!selectedCount.value || props.isLoading) return;
  showLabelSelector.value = !showLabelSelector.value;
};

const closeLabelSelector = () => {
  showLabelSelector.value = false;
};

const handleAssignLabels = labels => {
  emit('assignLabels', labels);
  closeLabelSelector();
};
</script>

<template>
  <div
    class="sticky top-0 z-10 bg-gradient-to-b from-n-background from-90% to-transparent px-6 pt-1 pb-2"
  >
    <div
      class="flex items-center gap-3 py-2 px-3 bg-n-alpha-1 rounded-lg justify-between"
    >
      <div class="flex items-center gap-3">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            :checked="allSelected"
            class="w-4 h-4 rounded border-n-strong text-n-brand focus:ring-n-brand"
            @change="toggleSelectAll"
          />
          <span class="text-sm text-n-slate-12">{{ selectAllLabel }}</span>
        </label>
        <span v-if="selectedCount" class="text-sm text-n-slate-11">
          {{ selectedCountLabel }}
        </span>
      </div>

      <div class="flex items-center gap-2">
        <Button
          sm
          ghost
          slate
          :label="t('CONTACTS_BULK_ACTIONS.CLEAR_SELECTION')"
          class="!px-1.5"
          @click="emitClearSelection"
        />
        <div
          v-on-click-outside="closeLabelSelector"
          class="relative flex items-center"
        >
          <Button
            sm
            faded
            slate
            icon="i-lucide-tags"
            :label="t('CONTACTS_BULK_ACTIONS.ASSIGN_LABELS')"
            :disabled="!selectedCount || isLoading"
            :is-loading="isLoading"
            class="[&>span:nth-child(2)]:hidden sm:[&>span:nth-child(2)]:inline w-fit"
            @click="toggleLabelSelector"
          />
          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="transform opacity-0 scale-95"
            enter-to-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="transform opacity-100 scale-100"
            leave-to-class="transform opacity-0 scale-95"
          >
            <LabelActions
              v-if="showLabelSelector"
              class="[&>.triangle]:!hidden [&>div>button]:!hidden ltr:!right-0 rtl:!left-0 top-8 mt-0.5"
              @assign="handleAssignLabels"
            />
          </transition>
        </div>
        <Policy :permissions="['administrator']">
          <Button
            v-tooltip.bottom="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
            sm
            faded
            ruby
            icon="i-lucide-trash"
            :label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
            :aria-label="t('CONTACTS_BULK_ACTIONS.DELETE_CONTACTS')"
            :disabled="!selectedCount || isLoading"
            :is-loading="isLoading"
            class="!px-1.5 [&>span:nth-child(2)]:hidden"
            @click="emit('deleteSelected')"
          />
        </Policy>
      </div>
    </div>
  </div>
</template>
